import type { PrizeDraw, PrizeOption } from "@/domain/marketing/api"
import { SurfaceProvider } from "@/components/ui"
import { WHEEL_MISS_ID, wheelLandingIndex, wheelLandingRotation, wheelSegments } from "@/domain/marketing/prize-game"
import { useId } from "react"
import { WheelConfetti } from "./PrizeWheelConfetti"
import { WheelFace } from "./PrizeWheelFace"
import { WheelFront, WheelHub, WheelPointer, WheelRim } from "./PrizeWheelFrame"
import { wheelToneKinds } from "./prize-wheel-palette"
import { useWheelSpin } from "./use-wheel-spin"

type PrizeWheelProps = {
  prizes: readonly PrizeOption[]
  missPossible?: boolean
  draw: PrizeDraw | null
  logoUrl?: string
  storeName: string
  onSpinEnd: () => void
}

/** Prize wheel in a fixed palette that sits next to any brand (ink and ivory slices, champagne-gold rim with bulbs, ticking pointer, the store logo as the only brand element in the hub); spins to the draw of the server, never deciding it, marks the landed slice (confetti on a prize) and lands at once with reduced motion. */
export function PrizeWheel({ prizes, missPossible = true, draw, logoUrl, storeName, onSpinEnd }: PrizeWheelProps) {
  const segments = wheelSegments(prizes, missPossible)
  const uid = `wheel${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`
  const kinds = wheelToneKinds(segments.map((segment) => segment.id === WHEEL_MISS_ID))
  const slices = segments.map((segment, index) => ({ ...segment, kind: kinds[index] ?? "ink" }))
  const landing = draw ? wheelLandingIndex(segments, draw) : null
  const target = landing === null ? null : wheelLandingRotation(segments.length, landing)
  const won = landing !== null && segments[landing]?.id !== WHEEL_MISS_ID
  const wheel = useWheelSpin({ spinKey: draw, target, count: segments.length, won, onSpinEnd })
  const landed = landing !== null && wheel.landed ? landing : null

  return (
    <SurfaceProvider surface="game">
      <div className="font-game-body relative mx-auto aspect-square w-full max-w-game-wheel">
        <ul className="sr-only" aria-label="Fatias da roleta">
          {segments.map((segment, index) => (
            <li key={segment.id} aria-current={index === landed ? "true" : undefined}>
              {segment.label}
            </li>
          ))}
        </ul>
        <WheelRim uid={uid} bulbsRef={wheel.bulbsRef} />
        <div
          ref={wheel.rotorRef}
          aria-hidden
          className="absolute inset-0"
          style={wheel.instant && target !== null ? { transform: `rotate(${target % 360}deg)` } : undefined}
        >
          <WheelFace
            uid={uid}
            slices={slices}
            landing={landed === null ? null : { index: landed, won, animate: !wheel.instant }}
          />
        </div>
        <WheelFront uid={uid} />
        <WheelHub logoUrl={logoUrl} storeName={storeName} />
        <WheelPointer uid={uid} pointerRef={wheel.pointerRef} />
        {landed !== null && won && !wheel.instant ? <WheelConfetti /> : null}
      </div>
    </SurfaceProvider>
  )
}
